/**
 * fbp-eu-vat-display.js - § 19 UStG (Kleinunternehmerregelung) tax note for EU visitors.
 *
 * Until 2026-08-24 FBP collected destination-country VAT under EU OSS, and this script rewrote
 * every displayed price to the VAT-inclusive figure (net x (1 + rate)) with an
 * "incl. 19% MwSt ..." note, because German price-display law (PAngV) requires the total price
 * to be the prominent figure.
 *
 * Since 2026-08-24 FBP operates under Germany's small-business regulation (§ 19 UStG,
 * Kleinunternehmerregelung): no VAT is charged on any order and checkout collects none, so the
 * listed price already IS the total price for every visitor. Prices are therefore no longer
 * rewritten. What remains is a short note under the price telling EU visitors WHY no VAT is
 * shown anywhere: § 19 UStG.
 *
 * The EU visitor is still detected from the browser timezone rather than IP or Liquid: the
 * storefront HTML is cached identically worldwide (verified 2026-07-28: Shopify.country = "US"
 * from a German IP), so the server can never know the country, and the timezone check is
 * cache-proof and needs no network call. Non-EU visitors see no note at all, exactly as before.
 *
 * COMPATIBILITY: window.FBP_EU_VAT is still published (now always null) and fbp:vat-ready still
 * fires, because other components (daki-head-addon-upsell.js) read them to decide whether to
 * gross up their own figures. null puts every consumer on the plain-price path by construction.
 */
(function () {
  'use strict';

  // Same EU timezone coverage the old VAT gross-up used. Non-EU European zones (Europe/Zurich,
  // Europe/Oslo, Europe/London, Europe/Kyiv, Europe/Istanbul, Europe/Belgrade...) are
  // deliberately absent so those visitors stay untouched.
  var EU_ZONES = [
    'Europe/Vienna', 'Europe/Brussels', 'Europe/Sofia', 'Europe/Zagreb', 'Asia/Nicosia',
    'Europe/Nicosia', 'Europe/Prague', 'Europe/Copenhagen', 'Europe/Tallinn', 'Europe/Helsinki',
    'Europe/Paris', 'Europe/Berlin', 'Europe/Busingen', 'Europe/Athens', 'Europe/Budapest',
    'Europe/Dublin', 'Europe/Rome', 'Europe/Riga', 'Europe/Vilnius', 'Europe/Luxembourg',
    'Europe/Malta', 'Europe/Amsterdam', 'Europe/Warsaw', 'Europe/Lisbon', 'Atlantic/Madeira',
    'Atlantic/Azores', 'Europe/Bucharest', 'Europe/Bratislava', 'Europe/Ljubljana',
    'Europe/Madrid', 'Africa/Ceuta', 'Atlantic/Canary', 'Europe/Stockholm'
  ];
  var GERMAN_ZONES = { 'Europe/Berlin': 1, 'Europe/Busingen': 1 };

  var tz = null;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { /* no tz: no note */ }

  // § 19 UStG: nothing is grossed up anywhere any more. null = plain-price path for consumers.
  window.FBP_EU_VAT = null;
  try {
    document.dispatchEvent(new CustomEvent('fbp:vat-ready', { detail: null }));
  } catch (e) { /* older browsers: consumers fall back to reading window.FBP_EU_VAT */ }

  if (!tz || EU_ZONES.indexOf(tz) < 0) return;   // non-EU visitor: change absolutely nothing.

  var NOTE = GERMAN_ZONES[tz]
    ? 'Keine Umsatzsteuer gemäß § 19 UStG (Kleinunternehmerregelung)'
    : 'No VAT charged — § 19 UStG (German small business regulation)';

  var PROCESSED = 'data-fbp-vat-done';

  // Only elements that genuinely render a price - the same set the old gross-up rewrote, so the
  // note appears exactly where "incl. 19% MwSt" used to sit. Deliberately conservative: a missed
  // price is a cosmetic inconsistency, but noting something that is not a price is a real bug.
  var SELECTORS = [
    '#ProductPrice',
    '.price-box .new-price',
    '.price-item--last',
    // NOT the sticky bar: it mirrors #ProductPrice. NOT .fbp-daki-addon__price: that box renders
    // its own figures and its own note from window.FBP_EU_VAT (data-fbp-vat-skip covers it too).
    '.total-price'
  ].join(',');

  function markup(el) {
    if (el.getAttribute(PROCESSED)) return;
    if (el.closest && el.closest('[data-fbp-vat-skip]')) return;   // component notes itself
    var text = (el.textContent || '').trim();
    if (!text || !/\d/.test(text)) return;         // price not rendered yet: retry on mutation
    el.setAttribute(PROCESSED, '1');
    var note = document.createElement('span');
    note.className = 'fbp-vat-note';
    // flex-basis:100% puts the note on its own line when the price sits in a flex row (product
    // page); it is inert in normal block flow (collection tiles).
    note.setAttribute('style',
      'display:block;flex-basis:100%;font-weight:400;font-size:12px;line-height:1.35;opacity:.75;margin-top:2px;min-width:0;white-space:normal;overflow-wrap:anywhere;');
    note.textContent = NOTE;
    if (el.parentNode) el.parentNode.insertBefore(note, el.nextSibling);
  }

  function run() {
    // Search pages carry a hidden note; reveal it only for EU visitors, who are the only ones reaching this code.
    var taxNotes = document.querySelectorAll('.fbp-search-tax-note[hidden]');
    for (var t = 0; t < taxNotes.length; t++) { taxNotes[t].textContent = NOTE; taxNotes[t].hidden = false; }
    var nodes;
    try { nodes = document.querySelectorAll(SELECTORS); } catch (e) { return; }
    for (var i = 0; i < nodes.length; i++) { try { markup(nodes[i]); } catch (e) { /* never break the page */ } }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();

  // Prices re-render on variant change and in the cart drawer, so re-apply on DOM changes.
  try {
    var mo = new MutationObserver(function () { run(); });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) { /* observer unsupported: the initial pass still applied */ }
})();
